import { useEffect, useState } from "react";

const DURATION_MS = 550;

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface Easing {
  target: number[] | null;
  continuity: string | null;
  from: number[] | null;
  values: number[] | null;
}

/**
 * Moves what is drawn from where it was to `target`, easing out over about half a second, so an
 * answer is seen to move the dot and the line rather than swap them.
 *
 * Only when `continuity` is unchanged: a different group of portfolios or a different start month
 * is a different picture, and sliding between two pictures would draw a path nothing took.
 */
export function useEased(target: number[] | null, continuity: string | null): number[] | null {
  const [easing, setEasing] = useState<Easing>({ target, continuity, from: null, values: target });

  // Adjusted while rendering rather than in an effect, so the frame before the first step still
  // draws the old values instead of flashing the new ones.
  if (easing.target !== target) {
    const shown = easing.values;
    const eases =
      target !== null &&
      shown !== null &&
      continuity !== null &&
      continuity === easing.continuity &&
      shown.length === target.length &&
      !prefersReducedMotion();
    setEasing({ target, continuity, from: eases ? shown : null, values: eases ? shown : target });
  }

  const { from, target: to } = easing;
  useEffect(() => {
    if (from === null || to === null) return;

    const startedAt = performance.now();
    let request = 0;
    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / DURATION_MS);
      const eased = 1 - (1 - progress) ** 3;
      const values = to.map((value, index) => from[index] + (value - from[index]) * eased);
      setEasing((current) => (current.target === to ? { ...current, values } : current));
      if (progress < 1) request = requestAnimationFrame(step);
    };
    request = requestAnimationFrame(step);

    return () => cancelAnimationFrame(request);
  }, [from, to]);

  return easing.values;
}
